/**
 * mgvEngine.js
 * Utilitário para formatação e exportação de arquivos de carga para balanças
 * comerciais no padrão Toledo Prix / MGV (MGV5 / MGV6 / MGV7).
 *
 * Arquivo padrão gerado: ITENSMGV.TXT (Layout Versão 3/4)
 * Linha de tamanho fixo em caracteres/bytes:
 * DD(2) + T(1) + CCCCCC(6) + PPPPPP(6) + VVV(3) + D1(25) + D2(25) + RRRRRR(6) + FFFF(4) + IIIIII(6) + DV(1) + DE(1) + CF(4)
 * Quebra de linha obrigatória: CRLF (\r\n)
 */

export function sanitizeAscii(text) {
  if (!text) return '';
  return String(text)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove diacríticos/acentos
    .replace(/[^a-zA-Z0-9 \-_.,/]/g, ' ') // caracteres seguros
    .toUpperCase();
}

/**
 * Formata uma única linha para o produto no layout ITENSMGV.TXT
 */
export function formatMgvItemLine(item, options = {}) {
  const dept = String(item.department || options.department || 1).padStart(2, '0').slice(-2);

  // Tipo de venda: 0 = Peso (KG), 1 = Unidade (UN)
  let saleType = '0';
  if (item.scaleType !== undefined && item.scaleType !== null) {
    saleType = String(item.scaleType);
  } else if (item.unit) {
    const u = String(item.unit).toLowerCase().trim();
    saleType = u === 'kg' || u === 'g' || u === 'quilo' || u === 'kilo' ? '0' : '1';
  } else if (options.defaultSaleType !== undefined) {
    saleType = String(options.defaultSaleType);
  }

  // Código PLU (6 dígitos com zeros à esquerda) baseado no ID do produto
  const plu = String(item.id || 0).padStart(6, '0').slice(-6);

  // Preço de venda em centavos (6 dígitos)
  const priceVal = parseFloat(item.value || item.valueUn || 0);
  const priceCents = Math.round(priceVal * 100);
  const price = String(priceCents).padStart(6, '0').slice(-6);

  // Dias de validade (3 dígitos: 000 = não imprime validade)
  const shelfLifeVal = parseInt(item.shelfLifeDays !== undefined ? item.shelfLifeDays : (options.defaultShelfLifeDays || 0), 10);
  const shelfLife = String(isNaN(shelfLifeVal) ? 0 : shelfLifeVal).padStart(3, '0').slice(-3);

  // Descrições divididas em Linha 1 (25 chars) e Linha 2 (25 chars)
  const cleanName = sanitizeAscii(item.name || 'PRODUTO').trim();
  const desc1 = cleanName.substring(0, 25).padEnd(25, ' ');
  const desc2 = cleanName.substring(25, 50).padEnd(25, ' ');

  // Campos adicionais padrão (Receita, Fornecedor, Imagem, Flags, Nutricional)
  const receita = '000000'; // 6 dígitos
  const fornecedor = '0000'; // 4 dígitos
  const imagem = '000000'; // 6 dígitos
  const dataValidade = '0'; // 1 dígito
  const dataEmbalagem = '0'; // 1 dígito
  const tabelaNutricional = '0000'; // 4 dígitos

  return `${dept}${saleType}${plu}${price}${shelfLife}${desc1}${desc2}${receita}${fornecedor}${imagem}${dataValidade}${dataEmbalagem}${tabelaNutricional}`;
}

/**
 * Gera o conteúdo textual completo para o arquivo ITENSMGV.TXT a partir de uma lista de produtos
 */
export function generateItensMgvTxt(items = [], options = {}) {
  if (!items || items.length === 0) {
    return '';
  }

  const lines = items.map((item) => formatMgvItemLine(item, options));
  return lines.join('\r\n') + '\r\n';
}

/**
 * Dispara o download client-side do arquivo de carga gerado
 */
export function downloadMgvFile(content, fileName = 'ITENSMGV.TXT') {
  if (!content) return;

  const blob = new Blob([content], { type: 'text/plain;charset=windows-1252' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

